import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../material.module';
import { WellAttachmentsService } from '../../../services/well-attachments.service';

interface UploadRow {
    id: number;
    file: File | null;
    description: string;
}

export interface UploadAttachmentsDialogData {
    wellId: number;
    wellName: string;
}

@Component({
    selector: 'app-upload-attachments-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, MaterialModule],
    templateUrl: './upload-attachments-dialog.component.html',
    styleUrls: ['./upload-attachments-dialog.component.css']
})
export class UploadAttachmentsDialogComponent {
    rows: UploadRow[] = [];
    isUploading = false;
    errorMessage = '';

    private nextId = 1;
    private readonly wellAttachmentsService = inject(WellAttachmentsService);

    constructor(
        public dialogRef: MatDialogRef<UploadAttachmentsDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: UploadAttachmentsDialogData
    ) {
        this.addRow();
    }

    addRow(): void {
        this.rows = [
            ...this.rows,
            {
                id: this.nextId++,
                file: null,
                description: '',
            },
        ];
    }

    removeRow(rowId: number): void {
        this.rows = this.rows.filter((r) => r.id !== rowId);
        if (this.rows.length === 0) {
            this.addRow();
        }
    }

    onFileSelected(event: Event, rowId: number): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;

        const target = this.rows.find((r) => r.id === rowId);
        if (target) {
            target.file = file;
            this.rows = [...this.rows];
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    onDrop(event: DragEvent, rowId: number): void {
        event.preventDefault();
        event.stopPropagation();
        const target = this.rows.find((r) => r.id === rowId);
        if (!target) return;
        const file = event.dataTransfer?.files?.[0] ?? null;
        if (file) {
            target.file = file;
            this.rows = [...this.rows];
        }
    }

    upload(): void {
        if (this.isUploading) return;

        const emptyRows = this.rows.filter((r) => !r.file);
        if (emptyRows.length === this.rows.length) {
            this.errorMessage = 'Please select at least one file before uploading.';
            return;
        }
        if (emptyRows.length > 0) {
            this.errorMessage = 'All file sections must have a file selected before uploading.';
            return;
        }

        const formData = this.buildFormData();
        this.errorMessage = '';
        this.isUploading = true;
        this.dialogRef.disableClose = true;

        this.wellAttachmentsService.upload(this.data.wellId, formData).subscribe({
            next: () => {
                this.dialogRef.close(true);
            },
            error: () => {
                this.isUploading = false;
                this.dialogRef.disableClose = false;
                this.errorMessage = 'Upload failed. Please try again.';
            }
        });
    }

    close(): void {
        if (this.isUploading) return;
        this.dialogRef.close(false);
    }

    private buildFormData(): FormData {
        const formData = new FormData();
        this.rows
            .filter((r) => !!r.file)
            .forEach((r) => {
                formData.append('files', r.file as File);
                formData.append('descriptions', r.description.trim());
            });
        return formData;
    }
}
